import { Show, For, createMemo, createSignal, createEffect, type Component } from "solid-js";
import { PanelLayout } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { usePanel } from "@/shared/use-panel";
import { useWs } from "@/shared/ws";
import { PanelBadge } from "@/shared/panel-primitives";
import { registerPanel } from "@/panel-registry";
import { Skeleton } from "@/components/skeleton";
import { PanelChrome } from "../console/ui";
import "./registry.css";

// ─── Types ────────────────────────────────────────────────────────────────────

type Hive = "HKLM" | "HKCU" | "HKCR" | "HKU" | "HKCC";

type RegValueType = "String" | "ExpandString" | "Binary" | "DWord" | "QWord" | "MultiString";

interface Subkey {
  name: string; // chemin complet renvoyé par PS (ex: HKEY_LOCAL_MACHINE\Software\...)
  childName: string; // dernier segment
}

interface ListSubkeysResult {
  hive: Hive;
  path: string;
  subkeys: Subkey[];
}

interface RawValue {
  Name: string;
  Value: unknown;
  Type: RegValueType | string;
}

interface ValueRow {
  name: string;
  type: RegValueType | string;
  data: string;
  raw: unknown;
}

const HIVES: Hive[] = ["HKLM", "HKCU", "HKCR", "HKU", "HKCC"];

const VALUE_TYPES: RegValueType[] = ["String", "ExpandString", "DWord", "QWord", "Binary", "MultiString"];

const TYPE_LABELS: Record<string, string> = {
  String: "REG_SZ",
  ExpandString: "REG_EXPAND_SZ",
  Binary: "REG_BINARY",
  DWord: "REG_DWORD",
  QWord: "REG_QWORD",
  MultiString: "REG_MULTI_SZ",
};

const MAX_RENDERED_CHILDREN = 500;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normalizePath(raw: string): string {
  return raw
    .replace(/\//g, "\\")
    .replace(/^\\+|\\+$/g, "")
    .trim();
}

function joinPath(parent: string, child: string): string {
  const p = normalizePath(parent);
  const c = normalizePath(child);
  if (!p) return c;
  if (!c) return p;
  return `${p}\\${c}`;
}

function parentPath(path: string): string {
  const p = normalizePath(path);
  if (!p) return "";
  const idx = p.lastIndexOf("\\");
  if (idx === -1) return "";
  return p.slice(0, idx);
}

function lastSegment(path: string): string {
  const p = normalizePath(path);
  if (!p) return "";
  const idx = p.lastIndexOf("\\");
  return idx === -1 ? p : p.slice(idx + 1);
}

/** Clé unique pour le cache : `${hive}::${path}`. Path vide = racine. */
function cacheKey(hive: Hive, path: string): string {
  return `${hive}::${normalizePath(path)}`;
}

/** Convertit la `Value` brute du tool en chaîne lisible pour le tableau. */
function formatData(type: string, value: unknown): string {
  if (value === null || value === undefined) return "";
  if (type === "Binary") {
    if (Array.isArray(value)) {
      const bytes = value as number[];
      const head = bytes
        .slice(0, 32)
        .map((b) => b.toString(16).padStart(2, "0"))
        .join(" ");
      return bytes.length > 32 ? `${head} … (${bytes.length} octets)` : head;
    }
    return String(value);
  }
  if (type === "MultiString") {
    if (Array.isArray(value)) return (value as string[]).join(" | ");
    return String(value);
  }
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "bigint") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Encode la valeur saisie par l'opérateur dans le format attendu par registry_set. */
function encodeValueData(type: RegValueType, raw: string): unknown {
  switch (type) {
    case "DWord":
    case "QWord": {
      const trimmed = raw.trim();
      const n = trimmed.startsWith("0x") || trimmed.startsWith("0X") ? parseInt(trimmed, 16) : Number(trimmed);
      if (!Number.isFinite(n)) throw new Error(`Valeur entière invalide pour ${type}.`);
      return Math.trunc(n);
    }
    case "MultiString":
      return raw.split(/\r?\n/).filter((l) => l.length > 0);
    case "Binary": {
      const cleaned = raw.replace(/0x/gi, "").replace(/[,;]/g, " ").trim();
      const tokens = cleaned.split(/\s+/).filter((t) => t.length > 0);
      const bytes: number[] = [];
      if (tokens.length === 1 && tokens[0].length > 2 && /^[0-9a-f]+$/i.test(tokens[0])) {
        const hex = tokens[0].length % 2 === 0 ? tokens[0] : "0" + tokens[0];
        for (let i = 0; i < hex.length; i += 2) {
          bytes.push(parseInt(hex.slice(i, i + 2), 16));
        }
      } else {
        for (const t of tokens) {
          const b = /^[0-9a-f]+$/i.test(t) ? parseInt(t, 16) : Number(t);
          if (!Number.isFinite(b) || b < 0 || b > 255) {
            throw new Error(`Octet invalide: '${t}' (attendu 0-255).`);
          }
          bytes.push(b);
        }
      }
      return bytes;
    }
    default:
      return raw;
  }
}

/** Reformate la valeur brute renvoyée par le tool en string éditable. */
function rawToEditable(type: string, value: unknown): string {
  if (value === null || value === undefined) return "";
  if (type === "Binary" && Array.isArray(value)) {
    return (value as number[]).map((b) => b.toString(16).padStart(2, "0")).join(" ");
  }
  if (type === "MultiString" && Array.isArray(value)) {
    return (value as string[]).join("\n");
  }
  if (typeof value === "string") return value;
  return String(value);
}

/** Parse une chaîne hex éditable en tableau d'octets (tolérante : ignore les tokens vides). */
function parseHexBytes(raw: string): number[] {
  const cleaned = raw.replace(/0x/gi, "").replace(/[,;]/g, " ").trim();
  const tokens = cleaned.split(/\s+/).filter((t) => t.length > 0);
  const bytes: number[] = [];
  if (tokens.length === 1 && tokens[0].length > 2 && /^[0-9a-f]+$/i.test(tokens[0])) {
    const hex = tokens[0].length % 2 === 0 ? tokens[0] : "0" + tokens[0];
    for (let i = 0; i < hex.length; i += 2) bytes.push(parseInt(hex.slice(i, i + 2), 16));
    return bytes;
  }
  for (const t of tokens) {
    const b = /^[0-9a-f]+$/i.test(t) ? parseInt(t, 16) : Number(t);
    bytes.push(Number.isFinite(b) && b >= 0 && b <= 255 ? b : 0);
  }
  return bytes;
}

/** Sérialise un tableau d'octets en chaîne hex normalisée ("DE AD BE EF"). */
function bytesToHex(bytes: number[]): string {
  return bytes.map((b) => (b & 0xff).toString(16).padStart(2, "0")).join(" ");
}

/** Rendu ASCII d'un octet pour la colonne de droite de l'éditeur hex. */
function asciiOf(b: number): string {
  return b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : ".";
}

// ─── Panel ────────────────────────────────────────────────────────────────────

const RegistryPanel: Component = () => {
  const ws = useWs();
  const tools = useToolClient();
  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  // Hive sélectionnée pour la sélection rapide
  const [hive, setHive] = createSignal<Hive>("HKLM");

  // Sélection courante : la clé active dont on affiche les valeurs à droite.
  const [selectedHive, setSelectedHive] = createSignal<Hive>("HKLM");
  const [selectedPath, setSelectedPath] = createSignal<string>("");

  // Champ "chemin direct" dans la toolbar
  const [pathDraft, setPathDraft] = createSignal<string>("");

  // Recherche dans le tree
  const [search, setSearch] = createSignal<string>("");

  // Tree state — n'expose pas directement les Maps/Sets en signal pour forcer la
  // ré-évaluation : on les wrap dans un signal qu'on remplace.
  const [expanded, setExpanded] = createSignal<Set<string>>(new Set());
  const [loadingKeys, setLoadingKeys] = createSignal<Set<string>>(new Set());
  const [errorKeys, setErrorKeys] = createSignal<Map<string, string>>(new Map());
  const [subkeysCache, setSubkeysCache] = createSignal<Map<string, string[]>>(new Map());

  // Modale d'édition / création
  const [editing, setEditing] = createSignal<ValueRow | null>(null);
  const [creating, setCreating] = createSignal<boolean>(false);
  const [editName, setEditName] = createSignal<string>("");
  const [editType, setEditType] = createSignal<RegValueType>("String");
  const [editData, setEditData] = createSignal<string>("");
  const [editError, setEditError] = createSignal<string>("");

  // Éditeur hexadécimal (type Binary) — tableau d'octets édité dans la grille.
  const [hexBytes, setHexBytes] = createSignal<number[]>([]);

  // Nœud de l'arbre ayant le focus clavier (clé `${hive}::${path}`), pour la
  // navigation Haut/Bas/Gauche/Droite reproduisant l'explorateur Windows.
  const [focusedNode, setFocusedNode] = createSignal<string | null>(null);

  // ── Tree loading ──────────────────────────────────────────────────────────
  async function loadSubkeys(h: Hive, path: string, force = false): Promise<void> {
    const k = cacheKey(h, path);
    if (!force && subkeysCache().has(k)) return;
    if (loadingKeys().has(k)) return;

    setLoadingKeys((prev) => {
      const next = new Set(prev);
      next.add(k);
      return next;
    });
    setErrorKeys((prev) => {
      if (!prev.has(k)) return prev;
      const next = new Map(prev);
      next.delete(k);
      return next;
    });

    try {
      const res = await tools.call<ListSubkeysResult>("registry_list_subkeys", { hive: h, path: path || "\\" });
      const names = (res?.subkeys ?? [])
        .map((s) => s.childName)
        .filter((n) => n && n.length > 0)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
      setSubkeysCache((prev) => {
        const next = new Map(prev);
        next.set(k, names);
        return next;
      });
    } catch (err) {
      const msg = String((err as Error).message ?? err);
      setErrorKeys((prev) => {
        const next = new Map(prev);
        next.set(k, msg);
        return next;
      });
    } finally {
      setLoadingKeys((prev) => {
        const next = new Set(prev);
        next.delete(k);
        return next;
      });
    }
  }

  function toggleExpand(h: Hive, path: string): void {
    const k = cacheKey(h, path);
    const isOpen = expanded().has(k);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (isOpen) next.delete(k);
      else next.add(k);
      return next;
    });
    if (!isOpen) {
      void loadSubkeys(h, path);
    }
  }

  function selectKey(h: Hive, path: string): void {
    setSelectedHive(h);
    setSelectedPath(normalizePath(path));
    setPathDraft(normalizePath(path));
    setHive(h);
  }

  /**
   * Étend récursivement le chemin demandé puis sélectionne la feuille.
   * Charge les niveaux nécessaires si pas encore en cache.
   */
  async function expandToPath(h: Hive, fullPath: string): Promise<void> {
    const segments = normalizePath(fullPath).split("\\").filter(Boolean);
    let current = "";
    // Ouvre la racine
    setExpanded((prev) => {
      const next = new Set(prev);
      next.add(cacheKey(h, ""));
      return next;
    });
    await loadSubkeys(h, "");

    for (let i = 0; i < segments.length; i++) {
      current = current ? `${current}\\${segments[i]}` : segments[i];
      // Tous les niveaux intermédiaires sont expanded
      setExpanded((prev) => {
        const next = new Set(prev);
        next.add(cacheKey(h, current));
        return next;
      });
      // Charge les enfants pour pouvoir descendre
      if (i < segments.length - 1) {
        await loadSubkeys(h, current);
      }
    }
    selectKey(h, normalizePath(fullPath));
  }

  // PAS d'auto-chargement des racines. Le registre se navigue **à la demande** :
  // les racines de hive s'affichent repliées (simples libellés, aucun appel
  // outil) et `registry_list_subkeys` n'est émis que lorsque l'opérateur
  // déplie un nœud (`toggleExpand`) ou navigue explicitement (`expandToPath`).
  // Un effet d'auto-chargement, même « idempotent », a déjà provoqué une boucle
  // infinie d'appels (l'effet dépend de loadingKeys/errorKeys qu'il mute) qui
  // inondait l'agent — on ne réintroduit pas ce schéma.

  // ── Query des valeurs (panneau de droite) ─────────────────────────────────
  const valuesQuery = createToolQuery<RawValue[] | RawValue>(() => ({
    tool: "registry_get",
    params: { hive: selectedHive(), path: selectedPath() },
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const panel = usePanel(() => ({ isFetching: valuesQuery.isFetching, error: valuesQuery.error }));

  const valueRows = createMemo<ValueRow[]>(() => {
    const raw = valuesQuery.data;
    if (!raw) return [];
    const arr: RawValue[] = Array.isArray(raw) ? (raw as RawValue[]) : [raw as RawValue];
    return arr
      .filter((v) => v && typeof v === "object")
      .map((v) => ({
        name: v.Name ?? "",
        type: v.Type ?? "String",
        data: formatData(String(v.Type ?? ""), v.Value),
        raw: v.Value,
      }));
  });

  // Skeleton de l'arbre : tant qu'aucune racine de hive n'a fini de charger.
  const treeRootLoading = createMemo<boolean>(() => {
    if (!enabled()) return false;
    const cache = subkeysCache();
    const anyLoaded = HIVES.some((h) => cache.has(cacheKey(h, "")));
    const anyLoading = HIVES.some((h) => loadingKeys().has(cacheKey(h, "")));
    return !anyLoaded && anyLoading;
  });

  // ── Navigation helpers (toolbar) ──────────────────────────────────────────
  async function navigateToDraft(): Promise<void> {
    const p = normalizePath(pathDraft());
    await expandToPath(hive(), p);
  }

  function goUp(): void {
    const next = parentPath(selectedPath());
    selectKey(selectedHive(), next);
  }

  function goRoot(): void {
    selectKey(selectedHive(), "");
  }

  async function refreshAll(): Promise<void> {
    // Recharge tous les nœuds expanded actuellement
    const open = Array.from(expanded());
    for (const k of open) {
      const [h, ...rest] = k.split("::");
      const path = rest.join("::");
      await loadSubkeys(h as Hive, path, true);
    }
    await valuesQuery.refetch();
  }

  // ── Edit modal ────────────────────────────────────────────────────────────
  function openCreate(): void {
    setEditing(null);
    setCreating(true);
    setEditName("");
    setEditType("String");
    setEditData("");
    setHexBytes([]);
    setEditError("");
  }

  function openEdit(row: ValueRow): void {
    setCreating(false);
    setEditing(row);
    setEditName(row.name);
    const t = VALUE_TYPES.includes(row.type as RegValueType) ? (row.type as RegValueType) : "String";
    setEditType(t);
    const editable = rawToEditable(row.type, row.raw);
    setEditData(editable);
    setHexBytes(t === "Binary" ? parseHexBytes(editable) : []);
    setEditError("");
  }

  /** Change le type édité et resynchronise la représentation Binary <-> texte. */
  function changeEditType(next: RegValueType): void {
    const prev = editType();
    if (next === prev) return;
    if (next === "Binary") {
      // Hydrate la grille depuis le texte courant.
      setHexBytes(parseHexBytes(editData()));
    } else if (prev === "Binary") {
      // Repasse la grille en texte pour les éditeurs textuels.
      setEditData(bytesToHex(hexBytes()));
    }
    setEditType(next);
  }

  // ── Éditeur hexadécimal — mutations de la grille ──────────────────────────
  function setHexByteAt(index: number, hex: string): void {
    const cleaned = hex.replace(/[^0-9a-f]/gi, "").slice(0, 2);
    const value = cleaned.length > 0 ? parseInt(cleaned, 16) : 0;
    setHexBytes((prev) => {
      const next = prev.slice();
      if (index >= 0 && index < next.length) next[index] = value & 0xff;
      return next;
    });
  }

  function appendHexByte(): void {
    setHexBytes((prev) => [...prev, 0]);
  }

  function removeLastHexByte(): void {
    setHexBytes((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev));
  }

  function clearHexBytes(): void {
    setHexBytes([]);
  }

  function closeEdit(): void {
    setEditing(null);
    setCreating(false);
    setEditError("");
  }

  async function applyEdit(): Promise<void> {
    const name = editName().trim();
    if (!name) {
      setEditError("Le nom de la valeur est requis.");
      return;
    }
    const type = editType();
    let encoded: unknown;
    try {
      // Le type Binary est édité dans la grille hex : on encode les octets
      // directement, sans repasser par le parsing texte.
      encoded = type === "Binary" ? hexBytes().map((b) => b & 0xff) : encodeValueData(type, editData());
    } catch (err) {
      setEditError(String((err as Error).message ?? err));
      return;
    }

    const action = creating() ? "Créer" : "Modifier";
    const ok = await panel.confirm.confirm({
      title: `${action} la valeur — ${name}`,
      body: `${selectedHive()}\\${selectedPath()}\n${name} (${TYPE_LABELS[type] ?? type})`,
      level: "warn",
      confirmLabel: action,
    });
    if (!ok) return;

    panel.setActionStatus({ text: `${action}...`, level: "info" });
    try {
      await panel.tools.call(
        "registry_set",
        {
          hive: selectedHive(),
          path: selectedPath(),
          valueName: name,
          valueType: type,
          valueData: encoded,
          createPath: creating(),
        },
        { userConfirmedAt: new Date().toISOString() },
      );
      panel.setActionStatus(null);
      closeEdit();
      await valuesQuery.refetch();
    } catch (err) {
      const msg = String((err as Error).message ?? err);
      setEditError(msg);
      panel.setActionStatus({ text: `Erreur: ${msg}`, level: "danger" });
    }
  }

  // ── Delete ────────────────────────────────────────────────────────────────
  async function deleteValue(row: ValueRow): Promise<void> {
    const ok = await panel.confirm.confirm({
      title: `Supprimer la valeur — ${row.name || "(par défaut)"}`,
      body: `${selectedHive()}\\${selectedPath()}\n${row.name}\n\nCette opération est irréversible.`,
      level: "danger",
      entityName: row.name,
      confirmLabel: "Supprimer",
    });
    if (!ok) return;
    panel.setActionStatus({ text: "Suppression...", level: "info" });
    try {
      await panel.tools.call(
        "registry_delete",
        {
          hive: selectedHive(),
          path: selectedPath(),
          valueName: row.name,
          deleteKey: false,
        },
        { userConfirmedAt: new Date().toISOString() },
      );
      panel.setActionStatus(null);
      await valuesQuery.refetch();
    } catch (err) {
      panel.setActionStatus({ text: `Erreur: ${String(err)}`, level: "danger" });
    }
  }

  async function deleteSubkey(h: Hive, path: string): Promise<void> {
    const child = lastSegment(path);
    const ok = await panel.confirm.confirm({
      title: `Supprimer la clé — ${child}`,
      body: `${h}\\${path}\n\nLa clé et toutes ses sous-clés seront supprimées récursivement.`,
      level: "danger",
      entityName: child,
      confirmLabel: "Supprimer",
    });
    if (!ok) return;
    panel.setActionStatus({ text: "Suppression...", level: "info" });
    try {
      await panel.tools.call(
        "registry_delete",
        {
          hive: h,
          path,
          deleteKey: true,
        },
        { userConfirmedAt: new Date().toISOString() },
      );
      panel.setActionStatus(null);
      // Recharge le parent dans le tree, et reset sélection si on a tué la cible
      const parent = parentPath(path);
      await loadSubkeys(h, parent, true);
      if (selectedHive() === h && selectedPath() === path) {
        selectKey(h, parent);
      }
    } catch (err) {
      panel.setActionStatus({ text: `Erreur: ${String(err)}`, level: "danger" });
    }
  }

  // ── Recherche : un nœud est visible s'il match, OU si l'un de ses descendants
  // chargés match. On calcule un Set des chemins à garder visibles.
  const visiblePaths = createMemo<Set<string> | null>(() => {
    const q = search().trim().toLowerCase();
    if (!q) return null;
    const keep = new Set<string>();
    const cache = subkeysCache();
    // Parcours uniquement les nœuds connus (chargés).
    function visit(h: Hive, path: string): boolean {
      const k = cacheKey(h, path);
      let anyChildMatch = false;
      const children = cache.get(k);
      if (children) {
        for (const child of children) {
          const childPath = joinPath(path, child);
          if (visit(h, childPath)) anyChildMatch = true;
        }
      }
      const self = lastSegment(path).toLowerCase().includes(q);
      // Pour les racines de hive (path vide), on garde si le nom de hive match ou si un enfant match.
      const selfMatchAtRoot = !path && h.toLowerCase().includes(q);
      if (self || selfMatchAtRoot || anyChildMatch) {
        keep.add(k);
        return true;
      }
      return false;
    }
    for (const h of HIVES) visit(h, "");
    return keep;
  });

  // ── Fil d'Ariane (breadcrumbs) ────────────────────────────────────────────
  // Segments cliquables du chemin courant : { label, hive, path }.
  const breadcrumbSegments = createMemo<Array<{ label: string; path: string }>>(() => {
    const segs = normalizePath(selectedPath()).split("\\").filter(Boolean);
    const out: Array<{ label: string; path: string }> = [{ label: selectedHive(), path: "" }];
    let acc = "";
    for (const s of segs) {
      acc = acc ? `${acc}\\${s}` : s;
      out.push({ label: s, path: acc });
    }
    return out;
  });

  function navigateToBreadcrumb(path: string): void {
    void expandToPath(selectedHive(), path);
  }

  // ── Navigation clavier de l'arbre ─────────────────────────────────────────
  // Liste plate des nœuds visibles, dans l'ordre d'affichage, pour Haut/Bas.
  interface FlatNode {
    hive: Hive;
    path: string;
    key: string;
    level: number;
    expandable: boolean;
    expanded: boolean;
  }

  const flatNodes = createMemo<FlatNode[]>(() => {
    const out: FlatNode[] = [];
    const cache = subkeysCache();
    const exp = expanded();
    const filter = visiblePaths();
    const isVisible = (h: Hive, path: string): boolean => !filter || filter.has(cacheKey(h, path));

    function walk(h: Hive, path: string, level: number): void {
      const k = cacheKey(h, path);
      const children = cache.get(k);
      const isExp = exp.has(k);
      // Un nœud est "expandable" si on n'a pas encore exploré ses enfants ou
      // s'il en a au moins un (cohérent avec le chevron affiché par TreeNode).
      const expandable = children === undefined || children.length > 0;
      out.push({ hive: h, path, key: k, level, expandable, expanded: isExp });
      if (isExp && children) {
        for (const child of children) {
          const childPath = joinPath(path, child);
          if (isVisible(h, childPath)) walk(h, childPath, level + 1);
        }
      }
    }

    for (const h of HIVES) {
      if (isVisible(h, "")) walk(h, "", 0);
    }
    return out;
  });

  function focusNodeByIndex(idx: number): void {
    const nodes = flatNodes();
    if (nodes.length === 0) return;
    const clamped = Math.max(0, Math.min(nodes.length - 1, idx));
    const node = nodes[clamped];
    setFocusedNode(node.key);
    selectKey(node.hive, node.path);
  }

  /** Navigation clavier façon explorateur Windows sur l'arborescence. */
  function onTreeKeyDown(e: KeyboardEvent): void {
    const keys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
    if (!keys.includes(e.key)) return;
    e.preventDefault();

    const nodes = flatNodes();
    if (nodes.length === 0) return;
    const focusKey = focusedNode() ?? cacheKey(selectedHive(), selectedPath());
    let idx = nodes.findIndex((n) => n.key === focusKey);
    if (idx === -1) idx = 0;
    const node = nodes[idx];

    switch (e.key) {
      case "ArrowDown":
        focusNodeByIndex(idx + 1);
        break;
      case "ArrowUp":
        focusNodeByIndex(idx - 1);
        break;
      case "ArrowRight": {
        if (node.expandable && !node.expanded) {
          toggleExpand(node.hive, node.path);
        } else if (node.expanded) {
          // Déjà déplié → descend au premier enfant.
          focusNodeByIndex(idx + 1);
        }
        break;
      }
      case "ArrowLeft": {
        if (node.expanded) {
          toggleExpand(node.hive, node.path);
        } else if (node.path !== "") {
          // Remonte au parent.
          const parent = parentPath(node.path);
          const parentKey = cacheKey(node.hive, parent);
          const parentIdx = nodes.findIndex((n) => n.key === parentKey);
          if (parentIdx !== -1) focusNodeByIndex(parentIdx);
          else selectKey(node.hive, parent);
        }
        break;
      }
    }
  }

  // Garde le focus clavier aligné sur la sélection courante.
  createEffect(() => {
    const k = cacheKey(selectedHive(), selectedPath());
    if (focusedNode() === null) setFocusedNode(k);
  });

  // ── Tree node component ───────────────────────────────────────────────────
  interface NodeProps {
    hiveRoot: Hive;
    /** Chemin relatif à la hive ("" pour la racine de la hive). */
    path: string;
    level: number;
  }

  const TreeNode: Component<NodeProps> = (props) => {
    const k = () => cacheKey(props.hiveRoot, props.path);
    const isExpanded = () => expanded().has(k());
    const isLoading = () => loadingKeys().has(k());
    const err = () => errorKeys().get(k());
    const children = () => subkeysCache().get(k()) ?? [];
    const isSelected = () => selectedHive() === props.hiveRoot && selectedPath() === props.path;
    const label = () => (props.path === "" ? props.hiveRoot : lastSegment(props.path));

    const filteredChildren = createMemo<string[]>(() => {
      const all = children();
      const filter = visiblePaths();
      if (!filter) return all;
      return all.filter((c) => filter.has(cacheKey(props.hiveRoot, joinPath(props.path, c))));
    });

    const hiddenCount = createMemo(() => {
      const all = children().length;
      const shown = Math.min(filteredChildren().length, MAX_RENDERED_CHILDREN);
      const visible = filteredChildren().length;
      return { totalFiltered: visible, hiddenFromFilter: all - visible, hiddenFromCap: Math.max(0, visible - shown) };
    });

    const isFocused = () => focusedNode() === k();

    return (
      <div>
        <div
          class={isFocused() ? "reg-tree-row reg-tree-row--focused" : "reg-tree-row"}
          style={{
            display: "flex",
            "align-items": "center",
            gap: "2px",
            "padding-left": `${props.level * 12}px`,
            "padding-top": "2px",
            "padding-bottom": "2px",
            "border-radius": "3px",
            background: isSelected() ? "var(--surface-2, rgba(108,207,255,0.18))" : "transparent",
          }}
        >
          <button
            type="button"
            class="btn btn--xs btn--ghost"
            style={{
              "min-width": "18px",
              width: "18px",
              padding: "0",
              "justify-content": "center",
              opacity: 0.75,
            }}
            title={isExpanded() ? "Replier" : "Déplier"}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(props.hiveRoot, props.path);
            }}
          >
            {isLoading() ? "…" : isExpanded() ? "▼" : "▶"}
          </button>
          <button
            type="button"
            class="btn btn--xs btn--ghost"
            style={{
              flex: "1",
              "justify-content": "flex-start",
              "text-align": "left",
              "font-family": "ui-monospace, SFMono-Regular, Menlo, monospace",
              "font-size": "11px",
              overflow: "hidden",
              "text-overflow": "ellipsis",
              "white-space": "nowrap",
              color: isSelected() ? "var(--accent, #6cf)" : undefined,
              "font-weight": isSelected() ? 600 : undefined,
            }}
            title={`${props.hiveRoot}\\${props.path}`}
            onClick={() => {
              setFocusedNode(k());
              selectKey(props.hiveRoot, props.path);
            }}
          >
            {props.path === "" ? "🗝 " : "📁 "}
            {label()}
          </button>
          <Show when={props.path !== ""}>
            <button
              type="button"
              class="btn btn--xs btn--danger"
              style={{ "min-width": "20px", padding: "0 4px" }}
              title="Supprimer la clé (récursif)"
              onClick={(e) => {
                e.stopPropagation();
                void deleteSubkey(props.hiveRoot, props.path);
              }}
            >
              ✕
            </button>
          </Show>
        </div>
        <Show when={err() && isExpanded()}>
          <div
            style={{
              "padding-left": `${(props.level + 1) * 12 + 18}px`,
              "font-size": "10px",
              color: "var(--text-danger, #f88)",
            }}
          >
            ⚠ {err()}
          </div>
        </Show>
        <Show when={isExpanded() && !isLoading() && !err()}>
          <Show
            when={children().length > 0}
            fallback={
              <div
                style={{
                  "padding-left": `${(props.level + 1) * 12 + 18}px`,
                  "font-size": "10px",
                  opacity: 0.5,
                }}
              >
                (vide)
              </div>
            }
          >
            <For each={filteredChildren().slice(0, MAX_RENDERED_CHILDREN)}>
              {(child) => (
                <TreeNode hiveRoot={props.hiveRoot} path={joinPath(props.path, child)} level={props.level + 1} />
              )}
            </For>
            <Show when={hiddenCount().totalFiltered > MAX_RENDERED_CHILDREN}>
              <div
                style={{
                  "padding-left": `${(props.level + 1) * 12 + 18}px`,
                  "font-size": "10px",
                  opacity: 0.6,
                }}
              >
                {hiddenCount().totalFiltered - MAX_RENDERED_CHILDREN} clé(s) non affichée(s), affine ta recherche.
              </div>
            </Show>
          </Show>
        </Show>
      </div>
    );
  };

  // ── Colonnes tableau valeurs ──────────────────────────────────────────────
  const columns: Column<ValueRow>[] = [
    {
      accessorKey: "name",
      header: "Nom",
      cell: (info) => {
        const v = String(info.getValue() ?? "");
        return <>{v.length > 0 ? <code style={{ "font-size": "11px" }}>{v}</code> : <em>(par défaut)</em>}</>;
      },
    },
    {
      accessorKey: "type",
      header: "Type",
      cell: (info) => {
        const t = String(info.getValue() ?? "");
        return <PanelBadge intent="muted">{TYPE_LABELS[t] ?? t}</PanelBadge>;
      },
    },
    {
      accessorKey: "data",
      header: "Données",
      cell: (info) => (
        <span
          style={{
            "font-family": "ui-monospace, SFMono-Regular, Menlo, monospace",
            "font-size": "11px",
            "white-space": "nowrap",
            overflow: "hidden",
            "text-overflow": "ellipsis",
            "max-width": "420px",
            display: "inline-block",
          }}
          title={String(info.getValue() ?? "")}
        >
          {String(info.getValue() ?? "")}
        </span>
      ),
    },
  ];

  // Copie le chemin de registre courant dans le presse-papier.
  async function copyCurrentPath(): Promise<void> {
    const full = `${selectedHive()}\\${selectedPath() || ""}`.replace(/\\+$/, "");
    try {
      await navigator.clipboard.writeText(full);
      panel.setActionStatus({ text: "Chemin copié", level: "info" });
    } catch {
      panel.setActionStatus({ text: "Copie impossible", level: "danger" });
    }
    setTimeout(() => panel.setActionStatus(null), 2000);
  }

  // ── Toolbar ───────────────────────────────────────────────────────────────
  const actions = (
    <>
      <select
        class="panel-select"
        value={hive()}
        onChange={(e) => {
          const h = e.currentTarget.value as Hive;
          setHive(h);
          // Ouvre la racine de la hive sélectionnée pour visibilité
          setExpanded((prev) => {
            const next = new Set(prev);
            next.add(cacheKey(h, ""));
            return next;
          });
          void loadSubkeys(h, "");
        }}
      >
        <For each={HIVES}>{(h) => <option value={h}>{h}</option>}</For>
      </select>
      <input
        class="panel-filter-input"
        placeholder="Chemin (ex: SOFTWARE\\Microsoft\\Windows)"
        style={{ "min-width": "320px" }}
        value={pathDraft()}
        onInput={(e) => setPathDraft(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") void navigateToDraft();
        }}
      />
      <button class="btn btn--xs btn--primary" onClick={() => void navigateToDraft()}>
        Naviguer
      </button>
      <button class="btn btn--xs btn--ghost" onClick={goUp} disabled={!selectedPath()}>
        ↑ Parent
      </button>
      <button class="btn btn--xs btn--ghost" onClick={goRoot} disabled={!selectedPath()}>
        Racine
      </button>
      <button class="btn btn--xs btn--ghost" onClick={() => void refreshAll()}>
        ↺ Refresh
      </button>
      <button
        class="btn btn--xs btn--ghost"
        onClick={() => void copyCurrentPath()}
        title="Copier le chemin courant dans le presse-papier"
      >
        📋 Copier le chemin
      </button>
    </>
  );

  return (
    <PanelChrome icon="🗝" title="Registre Windows" tagType="core" actions={actions}>
      <PanelLayout title={`${selectedHive()}\\${selectedPath() || ""}`} status={panel.status()}>
        <div class="panel-split">
          {/* ── Tree à gauche ───────────────────────────────────────────── */}
          <aside
            class="panel-detail-slidein"
            style={{
              "max-width": "360px",
              "min-width": "260px",
              order: -1,
              display: "flex",
              "flex-direction": "column",
            }}
          >
            <div class="panel-detail-header">
              <h3>Arbre</h3>
              <button class="btn btn--xs btn--ghost" onClick={() => void refreshAll()} title="Recharger l'arbre">
                ↺
              </button>
            </div>
            <div
              class="panel-detail-section"
              style={{
                display: "flex",
                "flex-direction": "column",
                gap: "6px",
                "padding-top": "0",
              }}
            >
              <input
                class="panel-filter-input"
                placeholder="Rechercher..."
                value={search()}
                onInput={(e) => setSearch(e.currentTarget.value)}
                style={{ width: "100%" }}
              />
              <span style={{ "font-size": "10px", opacity: 0.6 }}>
                Filtre les clés chargées (étends pour voir plus).
              </span>
            </div>
            <div
              class="panel-detail-section reg-tree"
              tabindex="0"
              role="tree"
              aria-label="Arborescence du registre"
              onKeyDown={onTreeKeyDown}
              style={{
                flex: "1",
                overflow: "auto",
                "padding-top": "0",
              }}
            >
              <Show
                when={!treeRootLoading()}
                fallback={
                  <div style={{ padding: "8px 4px" }}>
                    <Skeleton variant="table" rows={6} />
                  </div>
                }
              >
                <For each={HIVES}>
                  {(h) => {
                    const visible = createMemo(() => {
                      const filter = visiblePaths();
                      if (!filter) return true;
                      return filter.has(cacheKey(h, ""));
                    });
                    return (
                      <Show when={visible()}>
                        <TreeNode hiveRoot={h} path="" level={0} />
                      </Show>
                    );
                  }}
                </For>
              </Show>
            </div>
          </aside>

          {/* ── Valeurs à droite ────────────────────────────────────────── */}
          <div class="panel-split-main">
            {/* Fil d'Ariane interactif : segments cliquables du chemin courant. */}
            <nav class="reg-breadcrumbs" aria-label="Fil d'Ariane du registre">
              <For each={breadcrumbSegments()}>
                {(seg, i) => {
                  const isLast = () => i() === breadcrumbSegments().length - 1;
                  return (
                    <>
                      <Show when={i() > 0}>
                        <span class="reg-breadcrumb-sep" aria-hidden="true">
                          {"\\"}
                        </span>
                      </Show>
                      <button
                        type="button"
                        class={isLast() ? "reg-breadcrumb reg-breadcrumb--current" : "reg-breadcrumb"}
                        aria-current={isLast() ? "page" : undefined}
                        disabled={isLast()}
                        title={isLast() ? undefined : `Naviguer vers ${seg.label}`}
                        onClick={() => {
                          if (!isLast()) navigateToBreadcrumb(seg.path);
                        }}
                      >
                        {seg.label}
                      </button>
                    </>
                  );
                }}
              </For>
            </nav>
            <div class="panel-toolbar panel-toolbar--filter" style={{ "justify-content": "space-between" }}>
              <span style={{ "font-size": "12px", opacity: 0.7 }}>{valueRows().length} valeur(s)</span>
              <button class="btn btn--xs btn--primary" onClick={openCreate} disabled={!enabled()}>
                + Nouvelle valeur
              </button>
            </div>
            <Show
              when={!valuesQuery.isLoading}
              fallback={
                <div style={{ padding: "8px 4px" }}>
                  <Skeleton variant="table" rows={6} />
                </div>
              }
            >
              <DataTable<ValueRow>
                rows={valueRows()}
                columns={columns}
                emptyMessage="Aucune valeur dans cette clé."
                rowActions={(r) => (
                  <>
                    <button class="btn btn--xs btn--ghost" onClick={() => openEdit(r)}>
                      Éditer
                    </button>
                    <button class="btn btn--xs btn--danger" onClick={() => deleteValue(r)}>
                      Supprimer
                    </button>
                  </>
                )}
              />
            </Show>
          </div>
        </div>

        {/* ── Modale création / édition ─────────────────────────────────── */}
        <Show when={editing() || creating()}>
          <div
            class="confirm-overlay"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeEdit();
            }}
          >
            <div class="confirm-card confirm--info" style={{ "min-width": "480px" }}>
              <header class="confirm-header">
                <span class="confirm-eyebrow">{creating() ? "Nouvelle valeur" : "Éditer la valeur"}</span>
                <h2 class="confirm-title">{`${selectedHive()}\\${selectedPath()}`}</h2>
              </header>
              <div class="confirm-body">
                <div style={{ display: "grid", gap: "12px" }}>
                  <label style={{ display: "flex", "flex-direction": "column", gap: "4px" }}>
                    <span style={{ "font-size": "11px", opacity: 0.7 }}>Nom</span>
                    <input
                      class="panel-filter-input"
                      value={editName()}
                      onInput={(e) => setEditName(e.currentTarget.value)}
                      disabled={!creating() && !!editing()}
                    />
                  </label>
                  <label style={{ display: "flex", "flex-direction": "column", gap: "4px" }}>
                    <span style={{ "font-size": "11px", opacity: 0.7 }}>Type</span>
                    <select
                      class="panel-select"
                      value={editType()}
                      onChange={(e) => changeEditType(e.currentTarget.value as RegValueType)}
                    >
                      <For each={VALUE_TYPES}>
                        {(t) => (
                          <option value={t}>
                            {TYPE_LABELS[t]} ({t})
                          </option>
                        )}
                      </For>
                    </select>
                  </label>
                  <label style={{ display: "flex", "flex-direction": "column", gap: "4px" }}>
                    <span style={{ "font-size": "11px", opacity: 0.7 }}>
                      Données
                      <Show when={editType() === "MultiString"}>
                        <em style={{ "margin-left": "6px" }}>(une chaîne par ligne)</em>
                      </Show>
                      <Show when={editType() === "Binary"}>
                        <em style={{ "margin-left": "6px" }}>(éditeur hexadécimal — offset / octets / ASCII)</em>
                      </Show>
                      <Show when={editType() === "DWord" || editType() === "QWord"}>
                        <em style={{ "margin-left": "6px" }}>(décimal ou 0x...)</em>
                      </Show>
                    </span>
                    <Show when={editType() === "Binary"}>
                      <div class="reg-hex">
                        <div class="reg-hex-toolbar">
                          <button type="button" class="btn btn--xs btn--ghost" onClick={appendHexByte}>
                            + Octet
                          </button>
                          <button
                            type="button"
                            class="btn btn--xs btn--ghost"
                            onClick={removeLastHexByte}
                            disabled={hexBytes().length === 0}
                          >
                            − Octet
                          </button>
                          <button
                            type="button"
                            class="btn btn--xs btn--ghost"
                            onClick={clearHexBytes}
                            disabled={hexBytes().length === 0}
                          >
                            Vider
                          </button>
                          <span class="reg-hex-meta">{hexBytes().length} octet(s)</span>
                        </div>
                        <Show
                          when={hexBytes().length > 0}
                          fallback={<div class="reg-hex-empty">Aucun octet. Utilise « + Octet » pour en ajouter.</div>}
                        >
                          <div class="reg-hex-grid">
                            <For each={Array.from({ length: Math.ceil(hexBytes().length / 16) })}>
                              {(_, rowIdx) => {
                                const base = () => rowIdx() * 16;
                                const slice = () => hexBytes().slice(base(), base() + 16);
                                return (
                                  <div class="reg-hex-line">
                                    <span class="reg-hex-offset">
                                      {base().toString(16).padStart(4, "0").toUpperCase()}
                                    </span>
                                    <div class="reg-hex-bytes">
                                      <For each={slice()}>
                                        {(b, colIdx) => {
                                          const absIdx = () => base() + colIdx();
                                          return (
                                            <input
                                              class="reg-hex-cell"
                                              maxLength={2}
                                              value={b.toString(16).padStart(2, "0")}
                                              onInput={(e) => setHexByteAt(absIdx(), e.currentTarget.value)}
                                              onFocus={(e) => e.currentTarget.select()}
                                            />
                                          );
                                        }}
                                      </For>
                                    </div>
                                    <span class="reg-hex-ascii">
                                      {slice()
                                        .map((x) => asciiOf(x))
                                        .join("")}
                                    </span>
                                  </div>
                                );
                              }}
                            </For>
                          </div>
                        </Show>
                      </div>
                    </Show>
                    <Show when={editType() === "MultiString"}>
                      <textarea
                        class="panel-filter-input"
                        style={{ "min-height": "80px", "font-family": "ui-monospace, monospace" }}
                        value={editData()}
                        onInput={(e) => setEditData(e.currentTarget.value)}
                      />
                    </Show>
                    <Show when={editType() !== "Binary" && editType() !== "MultiString"}>
                      <input
                        class="panel-filter-input"
                        value={editData()}
                        onInput={(e) => setEditData(e.currentTarget.value)}
                      />
                    </Show>
                  </label>
                  <Show when={editError()}>
                    <div class="ad-status" data-level="danger">
                      {editError()}
                    </div>
                  </Show>
                </div>
              </div>
              <footer class="confirm-footer">
                <button class="btn btn--sm btn--ghost" onClick={closeEdit}>
                  Annuler
                </button>
                <button class="btn btn--sm btn--info" onClick={applyEdit}>
                  {creating() ? "Créer" : "Enregistrer"}
                </button>
              </footer>
            </div>
          </div>
        </Show>
      </PanelLayout>
    </PanelChrome>
  );
};

registerPanel("registry", RegistryPanel);
export default RegistryPanel;
